/* eslint-disable no-console */
// services/leadWebSocketManager.js
import { buildTicketedWebSocketURL } from "../api/api";
import {
  clearWebSocketStatus,
  handleWebSocketClose,
  handleWebSocketTicketError,
} from "../auth/websocketLifecycle";

const SOCKET_PATH = "/ws/leads/";

class LeadWebSocketManager {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.reconnectTimeout = null;
    this.isConnecting = false;
    this.onMessageCallback = null; // 👈 add this line
    this.manualDisconnect = false;
    this.reconnectBlocked = false;
  }

  static getInstance() {
    if (!this.instance) {
      this.instance = new LeadWebSocketManager();
    }
    return this.instance;
  }

  setOnMessage(callback) {
    this.onMessageCallback = callback; // 👈 assign callback from component
  }

  async connect() {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) return;
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);

    this.isConnecting = true;
    this.manualDisconnect = false;
    this.reconnectBlocked = false;
    console.log("Connecting to leads WebSocket...");

    try {
      const wsUrl = await buildTicketedWebSocketURL(SOCKET_PATH);
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log("✅ Leads WebSocket connected");
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        clearWebSocketStatus(SOCKET_PATH);
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log("📩 New lead data:", data);

          // 👇 call listener if defined
          if (this.onMessageCallback) {
            this.onMessageCallback(data);
          }
        } catch (err) {
          console.error("Failed to parse lead data:", err, event.data);
        }
      };

      this.ws.onclose = (event) => {
        console.log("Leads WebSocket closed:", event.code, event.reason);
        this.isConnecting = false;
        if (!handleWebSocketClose(event, SOCKET_PATH)) {
          this.reconnectBlocked = true;
          if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
          return;
        }
        if (!this.manualDisconnect) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (err) => {
        console.error("Leads WebSocket error:", err);
        this.isConnecting = false;
        this.scheduleReconnect();
      };
    } catch (err) {
      console.error("Failed to connect:", err);
      this.isConnecting = false;
      if (handleWebSocketTicketError(err, SOCKET_PATH)) {
        this.scheduleReconnect();
      } else {
        this.reconnectBlocked = true;
      }
    }
  }

  scheduleReconnect() {
    if (this.reconnectBlocked || this.reconnectAttempts >= this.maxReconnectAttempts) return;
    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);
    console.log(`🔄 Reconnecting in ${delay}ms`);
    this.reconnectTimeout = setTimeout(() => {
      if (!this.manualDisconnect) this.connect();
    }, delay);
  }

  disconnect() {
    this.manualDisconnect = true; // ✅ prevent reconnection
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    if (this.ws) {
      this.ws.close(1000, "Manual disconnect");
      this.ws = null;
    }
    this.onMessageCallback = null; // 👈 clear callback
  }
}

export default LeadWebSocketManager.getInstance();
